import { callAct, isOk } from './apiConfig';

/*
 * sms — the "הודעות SMS" management screen's data layer, backed by the smsSettings table
 * + the getSmsInfo / saveSmsTemplates acts (owner-gated, business-scoped server-side).
 *
 * getSmsInfo   → { senderName, smsBalance, sentThisMonth, templates } — READ only. The balance
 *                and monthly-sent are owned by billing + the send pipeline; this screen never
 *                mutates them (there is no client-side credit change).
 * saveSmsTemplates → persists { senderName, templates } (the per-trigger templates + on/off +
 *                campaigns/lists) as one JSON blob. Balance is NOT sent (can't be spoofed).
 * sendTestMessage → the existing diagnostic act; "שלח בדיקה" wires straight to it.
 *
 * Everything degrades safely: acts that aren't reachable yet resolve to defaults, never throw.
 */

// The default per-trigger templates the screen ships with (Hebrew, RTL). `trigger` is a stable
// id (the point in the booking lifecycle); `triggerLabel` is what the card's chip shows. The
// message text uses {curly} variables the send pipeline will substitute.
export const DEFAULT_TEMPLATES = [
    { id: 'booking_confirmed', title: 'אישור תור', trigger: 'onCreate', triggerLabel: 'מיידי — בעת יצירת תור', enabled: true,
      message: 'שלום {שם_לקוח}, התור שלך ל{שירות} נקבע ל־{תאריך} בשעה {שעה}. נתראה!' },
    { id: 'reminder_24h', title: 'תזכורת 24 שעות', trigger: 'before24h', triggerLabel: '24 שעות לפני', enabled: true,
      message: 'תזכורת: יש לך תור מחר בשעה {שעה} ({שירות}). לביטול השב/י להודעה זו.' },
    { id: 'reminder_1h', title: 'תזכורת שעה לפני', trigger: 'before1h', triggerLabel: 'שעה לפני', enabled: false,
      message: 'התור שלך מתקרב! עוד כ־{דקות} דקות בשעה {שעה}. נתראה בקרוב.' },
    { id: 'waitlist_free', title: 'רשימת המתנה — תור פנוי', trigger: 'onWaitlist', triggerLabel: 'onWaitlist', enabled: true,
      message: 'שלום {שם_לקוח}, התפנה מקום! מיקומך בתור: {מיקום}. אשר/י הגעה בהקדם.' },
    { id: 'booking_cancel', title: 'ביטול תור', trigger: 'onCancel', triggerLabel: 'בעת ביטול', enabled: false,
      message: 'התור שלך ל־{תאריך} בשעה {שעה} בוטל. נשמח לראותך בפעם אחרת.' },
    { id: 'apology_cancel', title: 'הודעת התנצלות לביטול', trigger: 'onCancel', triggerLabel: 'בעת ביטול', enabled: false,
      message: 'מתנצלים, נאלצנו לבטל את התור שלך ל־{תאריך} בשעה {שעה}. צוות {שירות} לרשותך לתיאום מחדש.' },
    { id: 'feedback_after', title: 'משוב (יום אחרי)', trigger: 'dayAfter', triggerLabel: 'יום אחרי', enabled: false,
      message: 'שלום {שם_לקוח}, תודה שביקרת! נשמח לשמוע איך היה — דרגו אותנו: {קישור}' },
];

// The variables palette the editor offers to insert into a message.
export const TEMPLATE_VARS = ['{שם_לקוח}', '{תאריך}', '{שעה}', '{שירות}', '{דקות}', '{מיקום}', '{קישור}'];

// Default distribution lists / segments (client-side selection today — the broadcast act is
// still to come; see NOTE in the screen). Counts are filled from the real customers list.
export const DEFAULT_LISTS = [
    { id: 'all', name: 'כל הלקוחות' },
    { id: 'vip', name: 'VIP' },
    { id: 'recent', name: 'לקוחות אחרונים' },
];

// Fetch the business's SMS info + saved templates. Returns a fully-populated object with the
// default templates merged over whatever was saved, so the screen always has a complete grid.
export async function getSmsInfo() {
    const fallback = {
        senderName: 'HOPPA', smsBalance: 0, smsQuota: 1000, sentThisMonth: 0,
        templates: DEFAULT_TEMPLATES.map((t) => ({ ...t })),
    };
    try {
        const envelope = await callAct('getSmsInfo', {});
        if (isOk(envelope) && envelope.data) {
            const d = envelope.data;
            let saved = null;
            if (d.templates && typeof d.templates === 'string') {
                try { saved = JSON.parse(d.templates); } catch (e) { saved = null; }
            } else if (d.templates && typeof d.templates === 'object') {
                saved = d.templates;
            }
            return {
                senderName: d.senderName || 'HOPPA',
                smsBalance: Number(d.smsBalance) || 0,
                smsQuota: Math.max(Number(d.smsBalance) || 0, (saved && Number(saved.smsQuota)) || 1000),
                sentThisMonth: Number(d.sentThisMonth) || 0,
                templates: mergeTemplates(saved && saved.templates),
                campaignText: (saved && saved.campaignText) || '',
            };
        }
    } catch (e) { /* act not available */ }
    return fallback;
}

// Persist senderName + the templates/settings JSON. Returns { ok }. Never throws.
export async function saveSmsTemplates(senderName, templates, extra = {}) {
    try {
        const payload = { senderName: senderName || 'HOPPA', ...extra, templates };
        const envelope = await callAct('saveSmsTemplates', {
            senderName: payload.senderName,
            templates: JSON.stringify({ templates: payload.templates, campaignText: payload.campaignText || '', smsQuota: payload.smsQuota || 1000 }),
        });
        return { ok: isOk(envelope) };
    } catch (e) { return { ok: false }; }
}

// Send a test SMS to a phone (the existing owner-gated diagnostic act). Returns
// { ok, channel, note }.
export async function sendTest(phone, message) {
    try {
        const envelope = await callAct('sendTestMessage', { phone, message });
        if (isOk(envelope) && envelope.data) {
            return { ok: !!envelope.data.delivered, channel: envelope.data.channel || '', note: envelope.data.note || '' };
        }
        return { ok: false, channel: '', note: 'שליחה נכשלה' };
    } catch (e) { return { ok: false, channel: '', note: 'שגיאת רשת' }; }
}

// Merge saved templates (by id) over the defaults, keeping the default set complete/ordered.
function mergeTemplates(saved) {
    if (!Array.isArray(saved)) return DEFAULT_TEMPLATES.map((t) => ({ ...t }));
    const byId = {};
    saved.forEach((s) => { if (s && s.id) byId[s.id] = s; });
    return DEFAULT_TEMPLATES.map((def) => {
        const s = byId[def.id];
        if (!s) return { ...def };
        return {
            ...def,
            message: typeof s.message === 'string' ? s.message : def.message,
            enabled: typeof s.enabled === 'boolean' ? s.enabled : def.enabled,
        };
    });
}

export default { getSmsInfo, saveSmsTemplates, sendTest, DEFAULT_TEMPLATES, TEMPLATE_VARS, DEFAULT_LISTS };
